import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IAlternate } from '@df/catalog/model';
import { NOT_FOUND_REDIRECT_TO_HP_FLAG } from '@df/catalog/route/route.resolver';
import { MetaAlternateService } from '@df/core/meta/meta-alternate.service';
import { Multisite } from '@df/core/multisite/multisite.interface';
import { Events } from '@df/core/service/event';
import { IMultisiteItem, MultisiteService } from '@df/core/service/multisite';
import { Url } from '@df/core/service/url';
import find from 'lodash-es/find';
import { Acl } from '../../../dragonfly-core/src/service/acl';
import { Client } from '../../../dragonfly-core/src/service/client';
import { NCore } from '@df/core/df-core.interfaces';

@Injectable({
    providedIn: 'root'
})
export class MultisiteRedirectService {
    private coreConfig = inject(NCore.OPTIONS);
    /**
     * Class constructor with it'sdependencies
     */
    constructor(
        protected metaAlternateService: MetaAlternateService,
        protected router: Router,
        protected multisiteService: MultisiteService,
        protected url: Url,
        protected events: Events,
        private acl: Acl,
        private client: Client
    ) {
        if (
            this.coreConfig.multisite.redirect &&
            !this.multisiteService.noRedirect &&
            typeof window !== 'undefined' &&
            !this.acl.isDeveloper &&
            !this.client.isCms
        ) {
            const preferredStoreId = this.multisiteService.preferredStoreId;
            if (preferredStoreId && preferredStoreId !== this.multisiteService.current.siteId) {
                const url = this.getLink(preferredStoreId);
                if (url) {
                    window.location.href = url;
                }
            }
        }
    }

    /**
     * Redirect to page on another site
     * @returns {boolean} returns true of false depending on if redirect was executed
     */
    to(storeId: string | number | IMultisiteItem, options?: Partial<Multisite.IEventOptions>): boolean {
        const url = this.getLink(storeId, true);

        const msEvent: Multisite.IRedirectBeforeEvent = {
            url,
            continue: true,
            target: this.multisiteService.toMultisiteItem(storeId)!,
            next: () => {
                this.multisiteService.setPreferredStore(storeId);

                if (msEvent.url) {
                    window.location.href = msEvent.url;
                }
            },
            options: { ...options } as Multisite.IEventOptions
        };

        this.events.broadcast(Multisite.REDIRECT_BEFORE_EVENT, msEvent);
        if (msEvent.continue) {
            msEvent.next();
        }
        return !!url;
    }

    /**
     * Retrieves link where we should redirect user when switching to another store
     */
    getLink(storeId: string | number | IMultisiteItem, allowNonExact = false): string | undefined {
        const site = this.multisiteService.toMultisiteItem(storeId);
        // if no match no redirect can be applied
        if (!site) {
            return undefined;
        }

        // no redirect for current store
        if (site.id === this.multisiteService.current.id) {
            return undefined;
        }

        // try alternates
        const metaAlternateService = this.metaAlternateService;
        if (metaAlternateService) {
            const alternates = metaAlternateService.getAlternates();
            if (alternates) {
                const match = find(alternates, (alternate: IAlternate) => alternate.storeId === site.id);
                if (match) {
                    return match.href;
                }
            }
        }

        // return current path on another store
        return this.url.toUrl(allowNonExact ? this.router.url.replace(/#.*/, '') + '#' + NOT_FOUND_REDIRECT_TO_HP_FLAG : '', site.id);
    }
}
